import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  HelpOutline as HelpIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { retailerApi } from '../../services/api';

// Network view of currently-active crowdsourced stock overrides (Phase 4/5). A crowd
// override is active while web_stock_observed IS NOT NULL AND web_stock_observed_at >
// updated_at, so any feed parse supersedes it — the counts GROW through the day as the
// apply job runs and RESET after each retailer's morning parse. This page is the
// rollout-watching surface.
//
// TWO directions, one surface (the crowd veto pointing both ways):
//   veto -> sold-out  (web_stock_observed = false): suppress a size the crowd saw gone
//                      while the feed still says in-stock.
//   veto -> in-stock  (web_stock_observed = true):  serve a feed-sold-out size the crowd
//                      says is available (SO->AV — default OFF, auto-enables per the audit
//                      gate). Same machinery, opposite polarity.

// A small products/sizes drill-down table shared by both directions (identical columns).
function SizesTable({ sizes }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Product</TableCell>
          <TableCell>Size</TableCell>
          <TableCell>Colour</TableCell>
          <TableCell>Applied</TableCell>
          <TableCell>
            Parser last updated
            <Tooltip
              arrow
              title="When the retailer's feed last wrote this size. The override expires automatically once this passes the applied time (the next feed parse supersedes it)."
            >
              <HelpIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
            </Tooltip>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sizes.map((s, i) => (
          <TableRow key={i}>
            <TableCell>
              {s.product_url ? (
                <a href={s.product_url} target="_blank" rel="noopener noreferrer">{s.product_name}</a>
              ) : (s.product_name || '—')}
            </TableCell>
            <TableCell><strong>{s.size}</strong></TableCell>
            <TableCell>{s.raw_color || '—'}</TableCell>
            <TableCell>{s.applied_at ? new Date(s.applied_at).toLocaleString() : '—'}</TableCell>
            <TableCell>{s.parser_updated_at ? new Date(s.parser_updated_at).toLocaleString() : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Format an ISO suspension end-time as a short "until <local time>" for the tooltip.
function suspendedUntilLabel(iso) {
  if (!iso) return '';
  return `Auto-suspended until ${new Date(iso).toLocaleString()}. Crowd vetoes are not applied and matching serves the raw feed until this clears (or an admin clears it).`;
}

function RetailerVetoRow({ row, onCleared }) {
  const [open, setOpen] = useState(false);
  const [sizes, setSizes] = useState(null);
  const [staleUnvetoes, setStaleUnvetoes] = useState(null);
  const [instockSizes, setInstockSizes] = useState(null);
  const [staleInstock, setStaleInstock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const suspended = Boolean(row.suspended_until);

  const clearSuspension = async () => {
    setClearing(true);
    try {
      await retailerApi.clearStockSuspension(row.retailer_id);
      if (onCleared) onCleared();
    } catch (e) {
      // Surfaced via the page reload; leave the row so the operator retries.
    }
    setClearing(false);
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && sizes === null) {
      setLoading(true);
      try {
        const res = await retailerApi.activeVetoesForRetailer(row.retailer_id);
        setSizes(res.data?.sizes || []);
        setStaleUnvetoes(res.data?.stale_unvetoes || []);
        setInstockSizes(res.data?.instock_sizes || []);
        setStaleInstock(res.data?.stale_instock_overrides || []);
      } catch (e) {
        setSizes([]);
        setStaleUnvetoes([]);
        setInstockSizes([]);
        setStaleInstock([]);
      }
      setLoading(false);
    }
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={toggle}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell><strong>{row.retailer_id}</strong></TableCell>
        <TableCell>
          {suspended ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip arrow title={suspendedUntilLabel(row.suspended_until)}>
                <Chip
                  size="small"
                  color="error"
                  icon={<BlockIcon />}
                  label="Suspended"
                />
              </Tooltip>
              <Button size="small" variant="outlined" color="error" onClick={clearSuspension} disabled={clearing}>
                {clearing ? '…' : 'Clear'}
              </Button>
            </Box>
          ) : (
            <Chip size="small" color="success" variant="outlined" label="Active" />
          )}
        </TableCell>
        <TableCell align="right">
          <Tooltip arrow title="Vetoes currently live: sizes suppressed from matching because the crowd saw them sold out while the feed still says in-stock.">
            <Chip size="small" color="warning" label={row.active_vetoes} />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Tooltip arrow title="In-stock vetoes currently live: feed-sold-out sizes the crowd says are available, so we serve them (the SO→AV direction). Off by default — a retailer's in-stock direction turns on only once its audit trend + live pre-flight clear.">
            <Chip
              size="small"
              color={row.active_instock_vetoes > 0 ? 'info' : 'default'}
              variant={row.active_instock_vetoes > 0 ? 'filled' : 'outlined'}
              label={row.active_instock_vetoes || 0}
            />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Tooltip arrow title="Vetoes removed in the last 24h: the crowd saw the size available again after we vetoed it ('came back and stayed back'), so the veto was lifted and the size is served again.">
            <Chip
              size="small"
              color={row.lifted_24h > 0 ? 'success' : 'default'}
              variant={row.lifted_24h > 0 ? 'filled' : 'outlined'}
              label={row.lifted_24h > 0 ? `↑ ${row.lifted_24h}` : '0'}
            />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Tooltip arrow title="In-stock vetoes removed in the last 24h: the crowd flipped the size back to sold-out, so we stopped serving it (the mirror of a lifted veto).">
            <Chip
              size="small"
              color={row.deactivated_24h > 0 ? 'success' : 'default'}
              variant={row.deactivated_24h > 0 ? 'filled' : 'outlined'}
              label={row.deactivated_24h > 0 ? `↓ ${row.deactivated_24h}` : '0'}
            />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Tooltip arrow title="API-arbitrated decisions in the last 24h: for live_api retailers (Shopware/Shopify), the crowd only TRIGGERS a check — the platform Admin API decides, and its verdict is written here. Distinct from crowd decisions.">
            <Chip
              size="small"
              color={row.api_decisions_24h > 0 ? 'secondary' : 'default'}
              variant={row.api_decisions_24h > 0 ? 'filled' : 'outlined'}
              label={row.api_decisions_24h > 0 ? `⚡ ${row.api_decisions_24h}` : '0'}
            />
          </Tooltip>
        </TableCell>
        <TableCell align="right">{row.products}</TableCell>
        <TableCell>{row.newest_applied_at ? new Date(row.newest_applied_at).toLocaleString() : '—'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1, ml: 4 }}>
              {loading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  {/* --- veto -> sold-out --- */}
                  <Typography variant="overline" color="text.secondary">
                    Veto → sold-out · now suppressed
                  </Typography>
                  {sizes && sizes.length > 0 ? (
                    <SizesTable sizes={sizes} />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      No active vetoes.
                    </Typography>
                  )}

                  {staleUnvetoes && staleUnvetoes.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {staleUnvetoes.length} un-veto{staleUnvetoes.length === 1 ? '' : 's'} serving a dead match — needs attention
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        We lifted these vetoes, but the crowd now reads the size sold out and the feed still says in-stock — so they’re being served as matches while unavailable. The next apply run should re-veto them once ≥2 sessions confirm; if they persist, investigate.
                      </Typography>
                      <StaleTable rows={staleUnvetoes} timeLabel="Lifted" timeKey="lifted_at" />
                    </Alert>
                  )}

                  {/* --- veto -> in-stock (mirror) --- */}
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    Veto → in-stock · now served
                  </Typography>
                  {instockSizes && instockSizes.length > 0 ? (
                    <SizesTable sizes={instockSizes} />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      No active in-stock vetoes.
                    </Typography>
                  )}

                  {staleInstock && staleInstock.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {staleInstock.length} in-stock veto{staleInstock.length === 1 ? '' : 'es'} serving a size everyone now calls sold-out — needs attention
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        We’re serving these feed-sold-out sizes as available on the crowd’s word, but the crowd’s latest reading now says sold out — so we’re vouching for a size that’s gone. The next apply run should stop serving them once ≥2 sessions confirm; a persistent one is the signature of a bad selector reading sold-out as available — investigate.
                      </Typography>
                      <StaleTable rows={staleInstock} timeLabel="Served since" timeKey="applied_at" />
                    </Alert>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// The stale-alert table (products the divergence flags), shared by both directions —
// only the time column label/key differs.
function StaleTable({ rows, timeLabel, timeKey }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Product</TableCell>
          <TableCell>Size</TableCell>
          <TableCell>Colour</TableCell>
          <TableCell>{timeLabel}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((s, i) => (
          <TableRow key={i}>
            <TableCell>
              {s.product_url ? (
                <a href={s.product_url} target="_blank" rel="noopener noreferrer">{s.product_name}</a>
              ) : (s.product_name || '—')}
            </TableCell>
            <TableCell><strong>{s.size}</strong></TableCell>
            <TableCell>{s.raw_color || '—'}</TableCell>
            <TableCell>{s[timeKey] ? new Date(s[timeKey]).toLocaleString() : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function StockVetoesDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await retailerApi.activeVetoes();
      setData(res.data);
    } catch (e) {
      setError(e.body?.error || e.message || 'Failed to load active vetoes');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const retailers = data?.retailers || [];
  const total = data?.total_vetoes || 0;
  const totalInstock = data?.total_instock_vetoes || 0;
  const totalLifted = data?.total_lifted_24h || 0;
  const totalDeactivated = data?.total_deactivated_24h || 0;
  const totalApiDecisions = data?.total_api_decisions_24h || 0;
  const totalSuspended = data?.total_suspended || 0;
  const suspendedRetailers = retailers.filter((r) => r.suspended_until);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>Stock Vetoes</Typography>
        <Tooltip
          arrow
          title="Sizes where a corroborated crowd reading currently overrides the feed — either suppressing a size the crowd saw sold out (veto → sold-out) or serving a feed-sold-out size the crowd says is available (veto → in-stock). An override expires when the retailer's feed re-parses, so these counts grow through the day and reset after the morning parses."
        >
          <HelpIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {total > 0
          ? `${total} size${total === 1 ? '' : 's'} currently suppressed across ${retailers.length} retailer${retailers.length === 1 ? '' : 's'}. `
          : 'No sizes currently vetoed. '}
        {totalInstock > 0 && `${totalInstock} feed-sold-out size${totalInstock === 1 ? '' : 's'} served on the crowd’s word (veto → in-stock). `}
        {totalLifted > 0 && `${totalLifted} veto${totalLifted === 1 ? '' : 'es'} lifted in the last 24h (crowd saw the size back in stock). `}
        {totalDeactivated > 0 && `${totalDeactivated} in-stock veto${totalDeactivated === 1 ? '' : 'es'} removed in the last 24h (crowd flipped it back to sold-out). `}
        {totalApiDecisions > 0 && `${totalApiDecisions} decision${totalApiDecisions === 1 ? '' : 's'} arbitrated by the platform API in the last 24h (Shopware/Shopify — the crowd triggers, the API decides). `}
        Overrides expire automatically when each retailer’s feed re-parses — the counts grow through the day and reset after the morning parses.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {totalSuspended > 0 && (
        <Alert severity="error" icon={<BlockIcon />} sx={{ mb: 2 }}>
          {totalSuspended} retailer{totalSuspended === 1 ? '' : 's'} auto-suspended
          {' — '}
          {suspendedRetailers.map((r) => r.retailer_id).join(', ')}.
          {' '}
          Crowd vetoes are not applied for {totalSuspended === 1 ? 'it' : 'them'} until the suspension clears (drift/accuracy audit) or an admin clears it below.
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : retailers.length === 0 ? (
          <Box p={4}>
            <Typography variant="body2" color="text.secondary">
              No active overrides right now. This is the normal state outside the window between the apply job running and the next feed parse.
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell>Retailer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Veto → sold-out<br /><Typography variant="caption" color="text.secondary">now suppressed</Typography></TableCell>
                <TableCell align="right">Veto → in-stock<br /><Typography variant="caption" color="text.secondary">now served</Typography></TableCell>
                <TableCell align="right">Lifted (24h)<br /><Typography variant="caption" color="text.secondary">vetoes removed</Typography></TableCell>
                <TableCell align="right">Deactivated (24h)<br /><Typography variant="caption" color="text.secondary">in-stock removed</Typography></TableCell>
                <TableCell align="right">API (24h)<br /><Typography variant="caption" color="text.secondary">API-arbitrated</Typography></TableCell>
                <TableCell align="right">Products</TableCell>
                <TableCell>Latest applied</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {retailers.map((row) => (
                <RetailerVetoRow key={row.retailer_id} row={row} onCleared={load} />
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}

export default StockVetoesDashboard;
